import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ThemeConfig } from '../types/theme';

interface ThemeContextType {
  theme: ThemeConfig;
  updateTheme: (theme: Partial<ThemeConfig>) => void;
  setTheme: (theme: ThemeConfig) => void;
}

const defaultTheme: ThemeConfig = {
  backgroundImage: '',
  textColor: '#ffffff',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  opacity: 0.75,
  fontFamily: 'IntroRust'
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ 
  children, 
  initialTheme 
}: { 
  children: React.ReactNode;
  initialTheme?: ThemeConfig | null;
}) {
  const [theme, setTheme] = useState<ThemeConfig>(() => ({
    ...defaultTheme,
    ...(initialTheme || {}),
    // Ensure backgroundImage is properly initialized
    backgroundImage: initialTheme?.backgroundImage || defaultTheme.backgroundImage
  }));

  useEffect(() => {
    if (initialTheme) {
      setTheme({
        ...defaultTheme,
        ...initialTheme,
        // Ensure backgroundImage is preserved when updating from props
        backgroundImage: initialTheme.backgroundImage || defaultTheme.backgroundImage
      });
    }
  }, [initialTheme]);

  const updateTheme = (newTheme: Partial<ThemeConfig>) => {
    setTheme(current => ({
      ...current,
      ...newTheme,
      // Preserve backgroundImage unless explicitly provided
      backgroundImage: newTheme.backgroundImage !== undefined 
        ? newTheme.backgroundImage 
        : current.backgroundImage
    }));
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
}